import React, { useState, useEffect } from "react";
import { Sparkles, Loader2, Check, X, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

var API = process.env.REACT_APP_BACKEND_URL + "/api";

function TextImprover({ open, onClose, text, onApply }) {
  var _useState = useState(text);
  var originalText = _useState[0];
  var setOriginalText = _useState[1];
  
  var _useState2 = useState("");
  var improvedText = _useState2[0];
  var setImprovedText = _useState2[1];
  
  var _useState3 = useState("professional");
  var style = _useState3[0];
  var setStyle = _useState3[1];
  
  var _useState4 = useState(false);
  var isLoading = _useState4[0];
  var setIsLoading = _useState4[1];

  useEffect(function() {
    if (open) {
      setOriginalText(text);
      setImprovedText("");
    }
  }, [open, text]);

  function handleImprove() {
    if (!originalText || !originalText.trim()) {
      toast.error("Introduce un texto para mejorar");
      return;
    }
    setIsLoading(true);
    axios.post(API + "/improve-text", { text: originalText, style: style })
      .then(function(response) {
        setImprovedText(response.data.improved);
        toast.success("Texto mejorado");
      })
      .catch(function() {
        toast.error("Error al mejorar el texto");
      })
      .finally(function() {
        setIsLoading(false);
      });
  }

  function handleApply() {
    if (improvedText) {
      onApply(improvedText);
      toast.success("Texto aplicado");
    }
  }

  function handleClose() {
    setOriginalText("");
    setImprovedText("");
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl" data-testid="text-improver">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="text-purple-500" size={24} />
            Mejorar Texto con IA
          </DialogTitle>
          <DialogDescription>Mejora tu texto para hacerlo más profesional.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label className="text-sm mb-2 block">Estilo</Label>
            <Select value={style} onValueChange={setStyle}>
              <SelectTrigger className="input-elite" data-testid="style-select">
                <SelectValue placeholder="Estilo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="professional">Profesional</SelectItem>
                <SelectItem value="formal">Formal</SelectItem>
                <SelectItem value="friendly">Amigable</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm mb-2 block">Texto Original</Label>
            <Textarea value={originalText} onChange={function(e) { setOriginalText(e.target.value); }}
              placeholder="Texto a mejorar..." className="input-elite min-h-[100px]" data-testid="original-text" />
          </div>

          <div className="flex justify-center">
            <Button onClick={handleImprove} disabled={isLoading || !originalText} className="btn-primary gap-2" data-testid="improve-btn">
              {isLoading ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
              {isLoading ? "Mejorando..." : "Mejorar"}
            </Button>
          </div>

          {improvedText && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm">Texto Mejorado</Label>
                <Button variant="ghost" size="sm" onClick={handleImprove} className="text-purple-500" data-testid="retry-btn">
                  <RefreshCw size={14} className="mr-1" /> Reintentar
                </Button>
              </div>
              <div className="p-4 bg-green-50 border border-green-200 rounded">
                <p className="text-slate-700 whitespace-pre-wrap" data-testid="improved-text">{improvedText}</p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} className="btn-secondary gap-2" data-testid="cancel-btn">
            <X size={18} /> Cancelar
          </Button>
          <Button onClick={handleApply} disabled={!improvedText} className="btn-primary gap-2" data-testid="apply-btn">
            <Check size={18} /> Aplicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TextImprover;
